import React from "react";
import { Body, H6Css, Heading } from "../Typography";
import { mq } from "@/constants/breakpoints";
import { css } from "@emotion/react";
import { mutedBlueColor } from "@/constants/colors";
import Image from "next/image";
import { darkColor } from "@/constants/colors";
import { textColor } from "@/constants/colors";
import { contentfulLoader } from "../ContentfulImage/ContentfulImage";

interface FeatureCardWithIconProps {
  cardItem: {
    featureHeading: string;
    featureSubheading: string;
    cardIcon: string;
  };
  imageSrc: string;
  variant?: "horizontal" | "vertical";
  iconSize?: {
    width: number;
    height: number;
  };
  useIconContainer?: boolean;
  iconContainerSize?: {
    width: number;
    height: number;
  };
}

const FeatureCardWithIcon = ({
  cardItem,
  imageSrc,
  variant = "horizontal",
  iconSize = { width: 140, height: 150 },
  useIconContainer = false,
  iconContainerSize = { width: 80, height: 80 },
}: FeatureCardWithIconProps) => {
  return (
    <div
      key={cardItem.featureHeading}
      css={css`
        display: flex;
        align-items: center;
        background-color: ${mutedBlueColor};
        border-radius: 12px;
        padding: 40px 24px;
        text-align: center;
        width: 100%;
        min-width: 0;
        box-sizing: border-box;
        ${variant === "horizontal" &&
        `
          ${mq["xs"]} {
            flex-direction: column;
          }
          ${mq["sm"]} {
            flex-direction: row;
          }
          ${mq["md"]} {
            width: calc(50% - 16px);
          }
        `}
        ${variant === "vertical" &&
        `
          flex-direction: column;
        `}
      `}
    >
      {useIconContainer ? (
        <div
          css={css`
            width: ${iconContainerSize.width}px;
            height: ${iconContainerSize.height}px;
            border-radius: 50%;
            background: linear-gradient(
              129.16deg,
              rgba(255, 255, 255, 0.8) -68.9%,
              rgba(190, 220, 255, 0.8) 131.19%
            );
            border: 1px solid;
            border-image-source: linear-gradient(
              286.59deg,
              rgba(218, 236, 248, 0.34) 9.17%,
              rgba(218, 236, 248, 0) 109.76%
            );
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          <Image
            src={imageSrc}
            loader={contentfulLoader}
            alt="feature card icon"
            width={iconSize.width}
            height={iconSize.height}
            style={{
              width: `${iconSize.width}px`,
              height: "auto",
              maxHeight: `${iconSize.height}px`,
            }}
          />
        </div>
      ) : (
        <Image
          src={imageSrc}
          loader={contentfulLoader}
          alt="feature card icon"
          width={iconSize.width}
          height={iconSize.height}
        />
      )}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          ${variant === "horizontal" &&
          `
            margin-left: 60px;
            ${mq["xs"]} {
              margin-left: 0;
              align-items: center;
            }
            ${mq["sm"]} {
              margin-left: 32px;
            }
          `}
          ${variant === "vertical" &&
          `
            margin-top: 30px;
            align-items: center;
          `}
        `}
      >
        <Heading
          css={css`
            color: ${darkColor};
            ${H6Css}
            text-align: center;
            font-size: 20px;
            ${variant === "horizontal" &&
            `
              ${mq["sm"]} {
                text-align: left;
              }
            `}
            ${mq["lg"]} {
              font-size: 24px;
            }
          `}
        >
          {cardItem.featureHeading}
        </Heading>
        <Body
          css={css`
            margin-top: 16px;
            line-height: 140%;
            color: ${textColor};
            text-align: center;
            font-size: 14px;
            ${variant === "horizontal" &&
            `
              ${mq["sm"]} {
                text-align: left;
              }
            `}
          `}
        >
          {cardItem.featureSubheading}
        </Body>
      </div>
    </div>
  );
};

export default FeatureCardWithIcon;
